import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Check } from "lucide-react";

export type SortKey = "newest" | "updated" | "az" | "status" | "score";
export type SortDir = "asc" | "desc";

export const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "newest", label: "Newest" },
  { key: "updated", label: "Recently updated" },
  { key: "az", label: "A–Z" },
  { key: "status", label: "Status" },
  { key: "score", label: "Score" },
];

type Props = {
  value: SortKey;
  onChange: (v: SortKey) => void;
  direction: SortDir;
  onDirectionChange: (d: SortDir) => void;
  options?: { key: SortKey; label: string }[];
};

export function SortDropdown({ value, onChange, direction, onDirectionChange, options = SORT_OPTIONS }: Props) {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current && menuRef.current.contains(t)) return;
      if (btnRef.current && btnRef.current.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = options.find((o) => o.key === value) ?? options[0];

  return (
    <div className="sort-wrap">
      <button
        ref={btnRef}
        type="button"
        className="sort-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Sort"
      >
        <ArrowUpDown size={14} />
        <span className="sort-trigger-label">Sort:</span>
        <span className="sort-trigger-value">{current.label}</span>
      </button>
      <button
        type="button"
        className="sort-dir-btn"
        onClick={() => onDirectionChange(direction === "asc" ? "desc" : "asc")}
        aria-label={direction === "asc" ? "Ascending — click for descending" : "Descending — click for ascending"}
        title={direction === "asc" ? "Ascending" : "Descending"}
      >
        {direction === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
      </button>
      {open ? (
        <div ref={menuRef} className="sort-menu" role="listbox">
          {options.map((opt) => {
            const active = opt.key === value;
            return (
              <button
                key={opt.key}
                type="button"
                role="option"
                aria-selected={active}
                className={`sort-menu-item${active ? " is-active" : ""}`}
                onClick={() => {
                  onChange(opt.key);
                  setOpen(false);
                }}
              >
                <span>{opt.label}</span>
                {active ? <Check size={14} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
